(() => {
  const tagName = 'loecare-animated-counter';

  const parseNumber = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return null;
    const parsed = Number.parseInt(digits, 10);
    return Number.isSafeInteger(parsed) ? parsed : null;
  };

  const formatNumber = (value) =>
    new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(value);

  const JUMPS = [3, 2, 5, 4, 2, 4, 3, 5];
  const PAUSES = [0.78, 1.16, 0.9, 1.3, 0.84, 1.08, 0.96, 1.22];
  const STRIP_CHARS = [
    ' ',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
  ];

  if (!customElements.get(tagName)) {
    class LoecareAnimatedCounter extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          if (this.slots) this.arm();
          return;
        }
        this.initialized = true;

        this.fallback = this.querySelector('[data-counter-fallback]');
        this.odometer = this.querySelector('[data-counter-odometer]');
        this.digitsContainer = this.querySelector('[data-counter-digits]');
        this.prefixEl = this.querySelector(':scope > .loecare-counter__prefix');
        this.suffixEl = this.querySelector(':scope > .loecare-counter__suffix');
        this.start = parseNumber(this.dataset.start);
        this.pace = Math.min(10, Math.max(1, Number.parseFloat(this.dataset.pace) || 4));

        if (!this.fallback || !this.odometer || !this.digitsContainer || this.start === null) {
          return;
        }
        this.normalizeFallback();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.value = this.start;
        this.driftIndex = 0;
        this.runToken = 0;
        this.driftScheduled = false;

        this.onVisibility = () => this.updateDrift();
        this.onResize = () => this.paintGradient();

        this.build(this.start);
        this.snapTo(this.start);
        this.fallback.hidden = true;
        this.odometer.hidden = false;
        this.paintGradient();
        document.fonts?.ready?.then(() => this.paintGradient());

        this.arm();
      }

      disconnectedCallback() {
        if (this.slots) this.disarm();
      }

      arm() {
        window.addEventListener('resize', this.onResize);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.45;
            this.updateDrift();
          },
          { rootMargin: '0px 0px -80px 0px', threshold: 0.5 }
        );
        this.observer.observe(this);
      }

      disarm() {
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('visibilitychange', this.onVisibility);
        this.observer?.disconnect();
        this.runToken += 1;
        window.clearTimeout(this.timer);
        this.driftScheduled = false;
        this.slots?.forEach((slot) => window.clearTimeout(slot.normalizeTimer));
      }

      normalizeFallback() {
        const number = this.fallback.querySelector('.loecare-counter__fallback-number');
        if (number) number.textContent = formatNumber(this.start);
        this.updateAccessibleLabel(this.start);
      }

      build(sourceValue) {
        this.slots = [];
        this.digitsContainer.replaceChildren();

        [...formatNumber(sourceValue)].forEach((char) => {
          if (char >= '0' && char <= '9') {
            const cell = document.createElement('span');
            const strip = document.createElement('span');
            cell.className = 'loecare-counter__digit';
            strip.className = 'loecare-counter__strip';

            STRIP_CHARS.forEach((stripChar) => {
              const glyph = document.createElement('span');
              glyph.textContent = stripChar;
              strip.append(glyph);
            });

            cell.append(strip);
            this.digitsContainer.append(cell);
            this.slots.push({ strip, digit: null, position: 0, normalizeTimer: 0 });
            return;
          }

          const separator = document.createElement('span');
          separator.className = 'loecare-counter__separator';
          separator.textContent = char;
          this.digitsContainer.append(separator);
        });
      }

      setValue(value) {
        if (String(value).length > this.slots.length) {
          this.regrow(value);
          return;
        }

        this.value = value;
        const padded = String(value).padStart(this.slots.length, ' ');

        this.slots.forEach((slot, index) => {
          const char = padded[index];
          const digit = char === ' ' ? null : Number.parseInt(char, 10);
          if (slot.digit === digit) return;

          this.normalizeSlot(slot);

          let position;
          if (digit === null) {
            position = 0;
          } else if (slot.digit === null) {
            position = digit + 1;
          } else {
            position = slot.position + ((digit - slot.digit + 10) % 10);
          }

          slot.digit = digit;
          slot.position = position;
          slot.strip.style.transform = `translateY(${position * -1}em)`;
          if (position > 10) this.queueNormalize(slot);
        });

        this.updateAccessibleLabel(value);
      }

      regrow(value) {
        this.build(value);
        this.snapTo(value);
        this.paintGradient();
      }

      normalizeSlot(slot) {
        window.clearTimeout(slot.normalizeTimer);
        const canonicalPosition = slot.digit === null ? 0 : slot.digit + 1;
        if (slot.position === canonicalPosition) return;

        slot.position = canonicalPosition;
        slot.strip.classList.add('loecare-counter__strip--snap');
        slot.strip.style.transform = `translateY(${canonicalPosition * -1}em)`;
        void slot.strip.offsetHeight;
        slot.strip.classList.remove('loecare-counter__strip--snap');
      }

      queueNormalize(slot) {
        window.clearTimeout(slot.normalizeTimer);
        slot.normalizeTimer = window.setTimeout(() => this.normalizeSlot(slot), 190);
      }

      snapTo(value) {
        this.value = value;
        this.classList.add('loecare-counter--no-anim');
        const padded = String(value).padStart(this.slots.length, ' ');

        this.slots.forEach((slot, index) => {
          window.clearTimeout(slot.normalizeTimer);
          const char = padded[index];
          slot.digit = char === ' ' ? null : Number.parseInt(char, 10);
          slot.position = slot.digit === null ? 0 : slot.digit + 1;
          slot.strip.style.transform = `translateY(${slot.position * -1}em)`;
        });

        this.updateAccessibleLabel(value);
        void this.offsetWidth;
        this.classList.remove('loecare-counter--no-anim');
      }

      updateAccessibleLabel(value) {
        const prefix = this.prefixEl?.textContent || '';
        const suffix = this.suffixEl?.textContent || '';
        this.setAttribute('aria-label', `${prefix}${formatNumber(value)}${suffix}`);
      }

      updateDrift() {
        const shouldRun = this.inView && !document.hidden && this.isConnected;

        if (shouldRun && !this.driftScheduled) {
          this.scheduleDriftTick();
        } else if (!shouldRun && this.driftScheduled) {
          this.runToken += 1;
          window.clearTimeout(this.timer);
          this.driftScheduled = false;
        }
      }

      scheduleDriftTick() {
        this.driftScheduled = true;
        const token = this.runToken;
        const weight = PAUSES[this.driftIndex % PAUSES.length];

        this.timer = window.setTimeout(() => {
          if (token !== this.runToken) return;
          this.driftScheduled = false;
          if (!this.inView || document.hidden || !this.isConnected) return;

          const jump = JUMPS[(this.value + this.driftIndex) % JUMPS.length];
          this.driftIndex += 1;
          this.setValue(this.value + jump);
          this.scheduleDriftTick();
        }, Math.round(this.pace * 1000 * weight));
      }

      replay() {
        if (!this.slots) return;

        this.runToken += 1;
        window.clearTimeout(this.timer);
        this.driftScheduled = false;
        this.driftIndex = 0;
        this.build(this.start);
        this.snapTo(this.start);
        this.paintGradient();
        this.updateDrift();
      }

      paintGradient() {
        const box = this.getBoundingClientRect();
        if (!box.width || !this.classList.contains('lumin-text__fancy1')) return;

        this.style.setProperty('--counter-gradient-size', `${Math.round(box.width)}px 100%`);
        this.querySelectorAll(
          '.loecare-counter__digit, .loecare-counter__separator, .loecare-counter__prefix, .loecare-counter__suffix'
        ).forEach((element) => {
          const left = element.getBoundingClientRect().left - box.left;
          element.style.setProperty('--counter-gradient-pos', `${Math.round(-left)}px 0`);
        });
      }
    }

    customElements.define(tagName, LoecareAnimatedCounter);
  }

  if (!window.loecareAnimatedCounterEditorListener) {
    window.loecareAnimatedCounterEditorListener = true;
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      const counter = block.matches?.(tagName) ? block : block.querySelector?.(tagName);
      counter?.replay();
    });
  }
})();
